/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { MultipartUploadState, UploadStatus } from "./types";

/**
 * Upload a single part to S3 using the presigned URL.
 * Presigned S3 PUT URLs do not require sending Content-Type header unless presigned with it.
 */
export const uploadPartToS3 = async (
  file: File,
  partNumber: number,
  partSize: number,
  uploadUrl: string,
  onProgress?: (loaded: number) => void,
  signal?: AbortSignal
): Promise<string> => {
  const start = (partNumber - 1) * partSize;
  const end = Math.min(start + partSize, file.size);
  const blob = file.slice(start, end);

  const response = await axios.put(uploadUrl, blob, {
    signal,
    onUploadProgress: (event) => {
      if (event.total) {
        onProgress?.(event.loaded);
      }
    },
  });

  const rawETag = response.headers.etag || response.headers.ETag;
  if (!rawETag || typeof rawETag !== "string" || !rawETag.trim()) {
    throw new Error(`Missing ETag header in S3 upload response for part ${partNumber}`);
  }

  // Keep ETag as returned by S3 (strip surrounding quotes if present for API payload consistency)
  return rawETag.replace(/^"|"$/g, "").trim();
};

/**
 * Upload all multipart chunks to S3 with worker pool and ascending part sorting.
 */
export const uploadMultipartPartsToS3 = async (
  uploadState: MultipartUploadState,
  onProgress?: (state: MultipartUploadState) => void,
  refreshUploadUrls?: (
    uploadId: string,
    objectKey: string,
    partNumbers: number[]
  ) => Promise<{ partNumber: number; uploadUrl: string }[]>,
  abortUpload?: (
    uploadId: string,
    objectKey: string
  ) => Promise<void>,
  signal?: AbortSignal
): Promise<{ partNumber: number; eTag: string }[]> => {
  const { file, partSize } = uploadState;
  const calculateProgress = (uploadedBytes: number) =>
    file.size > 0
      ? Math.min(Math.round((uploadedBytes / file.size) * 100), 100)
      : 100;

  let currentState: MultipartUploadState = {
    ...uploadState,
  };

  const completedParts: { partNumber: number; eTag: string }[] = [];
  const uploadedPerPart = new Map<number, number>();
  const CONCURRENT_UPLOADS = 5;

  let isAborted = false;
  let hasAbortedBackend = false;
  const abortController = new AbortController();

  const triggerAbort = async () => {
    isAborted = true;
    abortController.abort();
    if (!hasAbortedBackend && abortUpload) {
      hasAbortedBackend = true;
      try {
        await abortUpload(currentState.uploadId, currentState.objectKey);
      } catch {
        // ignore abort failure
      }
    }
  };

  const handleAbort = () => {
    triggerAbort();
  };

  if (signal) {
    if (signal.aborted) {
      triggerAbort();
    } else {
      signal.addEventListener("abort", handleAbort);
    }
  }

  try {
    const uploadSinglePart = async (
      part: MultipartUploadState["parts"][number]
    ) => {
      if (isAborted) {
        throw new Error("Upload aborted");
      }

      let retryCount = 0;

      while (retryCount < 2) {
        if (isAborted) {
          throw new Error("Upload aborted");
        }

        try {
          currentState = {
            ...currentState,
            status: UploadStatus.UPLOADING,
            parts: currentState.parts.map((p) =>
              p.partNumber === part.partNumber
                ? {
                    ...p,
                    status: "uploading",
                  }
                : p
            ),
          };

          onProgress?.(currentState);

          const eTag = await uploadPartToS3(
            file,
            part.partNumber,
            partSize,
            part.uploadUrl,
            (loaded) => {
              if (isAborted) return;

              uploadedPerPart.set(part.partNumber, loaded);

              const uploadedBytes = [...uploadedPerPart.values()].reduce(
                (sum, bytes) => sum + bytes,
                0
              );

              currentState = {
                ...currentState,
                uploadedBytes,
                progress: calculateProgress(uploadedBytes),
              };

              onProgress?.(currentState);
            },
            abortController.signal
          );

          if (isAborted) {
            throw new Error("Upload aborted");
          }

          uploadedPerPart.set(
            part.partNumber,
            Math.min(partSize, file.size - (part.partNumber - 1) * partSize)
          );

          const uploadedBytes = [...uploadedPerPart.values()].reduce(
            (sum, bytes) => sum + bytes,
            0
          );

          currentState = {
            ...currentState,
            uploadedBytes,
            progress: calculateProgress(uploadedBytes),
            parts: currentState.parts.map((p) =>
              p.partNumber === part.partNumber
                ? {
                    ...p,
                    status: "completed",
                    eTag,
                  }
                : p
            ),
          };

          completedParts.push({
            partNumber: part.partNumber,
            eTag,
          });

          onProgress?.(currentState);
          return;
        } catch (error: any) {
          if (isAborted || axios.isCancel(error)) {
            throw error;
          }

          retryCount++;

          // Refresh URLs once
          if (retryCount === 1 && refreshUploadUrls && !isAborted) {
            try {
              const refreshedParts = await refreshUploadUrls(
                currentState.uploadId,
                currentState.objectKey,
                [part.partNumber]
              );

              if (isAborted) {
                throw new Error("Upload aborted");
              }

              const refreshedPart = refreshedParts?.find(
                (p) => p.partNumber === part.partNumber
              );

              if (!refreshedPart || !refreshedPart.uploadUrl) {
                throw new Error("Unable to refresh upload url");
              }

              currentState = {
                ...currentState,
                parts: currentState.parts.map((p) =>
                  p.partNumber === part.partNumber
                    ? {
                        ...p,
                        uploadUrl: refreshedPart.uploadUrl,
                      }
                    : p
                ),
              };

              part.uploadUrl = refreshedPart.uploadUrl;
              continue;
            } catch (refreshErr) {
              await triggerAbort();

              currentState = {
                ...currentState,
                status: UploadStatus.FAILED,
                errorMessage:
                  (refreshErr as any)?.response?.data?.message ??
                  (refreshErr as any)?.message ??
                  "Failed uploading file",
              };

              onProgress?.(currentState);
              throw refreshErr;
            }
          }

          // Retry failed -> Abort upload once
          await triggerAbort();

          currentState = {
            ...currentState,
            status: UploadStatus.FAILED,
            errorMessage:
              error?.response?.data?.message ??
              error?.message ??
              "Failed uploading file",
          };

          onProgress?.(currentState);
          throw error;
        }
      }
    };

    // Upload with a worker pool (max 5 concurrent uploads)
    let nextPartIndex = 0;

    const worker = async () => {
      while (!isAborted) {
        const currentIndex = nextPartIndex++;

        if (currentIndex >= currentState.parts.length) {
          return;
        }

        try {
          await uploadSinglePart(currentState.parts[currentIndex]);
        } catch (error) {
          isAborted = true;
          abortController.abort();
          throw error;
        }
      }
    };

    // Start workers using Promise.allSettled to ensure no unhandled promise rejections
    const workerPromises = Array.from(
      {
        length: Math.min(CONCURRENT_UPLOADS, currentState.parts.length),
      },
      () => worker()
    );

    const results = await Promise.allSettled(workerPromises);
    const rejected = results.find((r) => r.status === "rejected");

    if (rejected) {
      await triggerAbort();
      throw (rejected as PromiseRejectedResult).reason;
    }

    if (isAborted) {
      throw new Error("Upload aborted");
    }

    // Sort completed parts in ascending partNumber order as required by S3 CompleteMultipartUpload
    completedParts.sort((a, b) => a.partNumber - b.partNumber);

    return completedParts;
  } finally {
    if (signal) {
      signal.removeEventListener("abort", handleAbort);
    }
  }
};

/**
 * Build MultipartUploadState from Start API response.
 */
export const buildMultipartUploadState = (
  startApiResponseInput: any,
  file: File,
  interactionId: string
): MultipartUploadState => {
  const startApiResponse = startApiResponseInput?.uploadId
    ? startApiResponseInput
    : startApiResponseInput?.data || startApiResponseInput;

  if (
    !startApiResponse ||
    !startApiResponse.uploadId ||
    !startApiResponse.objectKey ||
    !startApiResponse.partSize ||
    !Array.isArray(startApiResponse.parts) ||
    startApiResponse.parts.length === 0
  ) {
    throw new Error("Invalid Start Multipart Upload API response structure");
  }

  return {
    uploadId: startApiResponse.uploadId,
    objectKey: startApiResponse.objectKey,
    interactionId,
    assetId: startApiResponse.assetId,
    file,
    fileName: file.name,
    fileSize: file.size,
    partSize: startApiResponse.partSize,
    uploadedBytes: 0,
    progress: 0,
    status: UploadStatus.STARTING,
    parts: startApiResponse.parts.map(
      (part: { partNumber: number; uploadUrl: string }) => ({
        partNumber: part.partNumber,
        uploadUrl: part.uploadUrl,
        status: "pending",
      })
    ),
  };
};
